import { useRef, useState } from "react";
import { Plus, Trash2, X, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useUploadEquipmentImage,
  useDeleteEquipmentImage,
} from "@/hooks/useEquipmentImages";
import type { EquipmentImage } from "@/hooks/useEquipment";

interface EquipmentImageGalleryProps {
  equipmentId: string;
  images: EquipmentImage[];
}

export const EquipmentImageGallery = ({
  equipmentId,
  images,
}: EquipmentImageGalleryProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const uploadImage = useUploadEquipmentImage();
  const deleteImage = useDeleteEquipmentImage();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (file.type.startsWith("image/")) {
        uploadImage.mutate({ equipmentId, file });
      }
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = (imageId: string, imageUrl: string) => {
    deleteImage.mutate({ imageId, imageUrl, equipmentId });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Images</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadImage.isPending}
          data-testid="add-equipment-image"
        >
          <Plus className="h-4 w-4 mr-2" />
          {uploadImage.isPending ? "Uploading..." : "Add Image"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
          data-testid="equipment-image-file-input"
        />
      </div>

      {images.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No images yet</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-equipment-images-empty"
          >
            Upload images
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="relative group aspect-square rounded-lg overflow-hidden border border-border bg-muted"
              data-testid={`equipment-image-${image.id}`}
            >
              <img
                src={image.image_url}
                alt=""
                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedImage(image.image_url)}
                data-testid={`equipment-image-preview-${image.id}`}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleDelete(image.id, image.image_url)}
                disabled={deleteImage.isPending}
                data-testid={`delete-equipment-image-${image.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4"
            onClick={() => setSelectedImage(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={selectedImage}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
